import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useCollection } from '@amzn/awsui-collection-hooks';
import Box from '@amzn/awsui-components-react/polaris/box';
import CollectionPreferences from '@amzn/awsui-components-react/polaris/collection-preferences';
import Pagination from '@amzn/awsui-components-react/polaris/pagination';
import SpaceBetween from '@amzn/awsui-components-react/polaris/space-between';
import StatusIndicator, {
  StatusIndicatorProps,
} from '@amzn/awsui-components-react/polaris/status-indicator';
import Table from '@amzn/awsui-components-react/polaris/table';
import Popover from '@amzn/awsui-components-react/polaris/popover';
import KeyValuePairs from '@amzn/awsui-components-react/polaris/key-value-pairs';
import {
  Attendance,
  Event,
  LocationType,
  Shift,
  Signup,
  WaitlistEntry,
} from '@amzn/red-velvet-api';
import { usePreferencesStrings } from '../../hooks/localization/preferencesStrings';
import { useLocalizedText } from '../../hooks/localizedText';
import { featureIsEnabled, useFeatures } from '../../hooks/features';
import {
  SEARCH_AND_ACTIVITY_SHIFT_TABLE,
  SHIFT_WAITLIST,
  SIGNUP_COHORT_CONTROL,
  SIGNUP_COHORT_TREATMENT,
  SIGNUP_EXPOSURE_METRIC,
  SignupSurface,
} from '../../utils/constants';
import { publishKatalMetric } from '../katalAnalytics';
import { ColumnMap, useTableColumns, SHIFT_NAME_COLUMN_WIDTH_PX } from '../../hooks/tableColumns';
import {
  doZonesDifferAtInstant,
  formatShiftDate,
  formatShiftTime,
  formatShiftTimeRangeWithZone,
  isValidTimeZone,
} from '../../utils/dateFormatting';
import { useLocale } from '../../hooks/locale';
import { useTimezone } from '../../hooks/timezone';
import { formatAttendanceTimeDisplay } from '../../utils/attendance/time';
import {
  buildShiftRow,
  getShiftLocationLabel,
  getVolunteerShiftSortComparators,
  isRowDimmed,
  isShiftFull,
  resolveLocationLabel,
  sortShiftRows,
} from '../../utils/volunteerEventShiftsTableUtils';
import {
  ShiftAction,
  ShiftStatus,
  UserShiftStatus,
  VolunteerEventShiftsTableProps,
  VolunteerShiftRow,
} from '../../utils/volunteerEventShiftsTableTypes';
import { CancelOrTrackActions, ShiftActions, ToggleWaitlist, TrackAttendanceLink } from './actions';
import { TruncatedCellText } from './truncatedCellText';

/** Default rows per page. The Event Details page passes a larger value (the carousel showed one row). */
export const DEFAULT_SHIFT_TABLE_PAGE_SIZE = 10;
/** Search shows a shorter default so one result card does not dominate the page (cce-7126). */
export const SEARCH_SHIFT_TABLE_PAGE_SIZE = 5;

/** Page-size options offered by the table's settings (gear) preferences. */
const SHIFT_TABLE_PAGE_SIZE_OPTIONS = [10, 25, 50];
/** Search adds a 5 so its shorter default is selectable in the gear. */
export const SEARCH_SHIFT_TABLE_PAGE_SIZE_OPTIONS = [
  SEARCH_SHIFT_TABLE_PAGE_SIZE,
  ...SHIFT_TABLE_PAGE_SIZE_OPTIONS,
];

/** i18n root for this table's labels (a single-token top-level key, matching the sibling tables). */
const TABLE_I18N = 'volunteerEventShiftsTable';

/**
 * These labels live in the shifts bundle. The trailing `translation` records where the same keys sit
 * on the legacy side; their i18next resolution comes from the global `fallbackNS` (see localizedText).
 *
 * useTableColumns and usePreferencesStrings still read through react-i18next, so the column headers
 * and the settings-gear text stay English until those two hooks move to useLocalizedText. They are
 * given this array so they land on the right bundle when they do, rather than on common.
 */
const TABLE_NAMESPACES = ['shifts', 'translation'];

const SHIFT_STATUS_INDICATOR: Record<ShiftStatus, StatusIndicatorProps.Type> = {
  [ShiftStatus.UPCOMING]: 'success',
  [ShiftStatus.IN_PROGRESS]: 'in-progress',
  [ShiftStatus.DEADLINE_PAST]: 'warning',
  [ShiftStatus.PAST]: 'stopped',
};

const USER_STATUS_INDICATOR: Record<UserShiftStatus, StatusIndicatorProps.Type> = {
  [UserShiftStatus.SIGNED_UP]: 'success',
  [UserShiftStatus.ON_THE_WAITLIST]: 'pending',
  [UserShiftStatus.MISSING_ATTENDANCE]: 'warning',
  [UserShiftStatus.NOT_SIGNED_UP]: 'stopped',
  [UserShiftStatus.ATTENDED]: 'success',
  [UserShiftStatus.DID_NOT_ATTEND]: 'stopped',
};

/** Greys out a cell's content for full shifts (matches the dimmed full rows in the mocks). */
function CellText({ dimmed, children }: { dimmed: boolean; children: React.ReactNode }) {
  return dimmed ? (
    <Box color="text-status-inactive" data-testid="dimmed-cell">
      {children}
    </Box>
  ) : (
    <>{children}</>
  );
}

function ShiftStatusCell({ status }: { status: ShiftStatus }) {
  const { t } = useLocalizedText(TABLE_NAMESPACES);
  return (
    <StatusIndicator type={SHIFT_STATUS_INDICATOR[status]}>
      {t(`${TABLE_I18N}.shiftStatus.${status}`)}
    </StatusIndicator>
  );
}

function UserStatusCell({
  status,
  attendanceMinutes,
}: {
  status: UserShiftStatus;
  attendanceMinutes?: number;
}) {
  const { t } = useLocalizedText(TABLE_NAMESPACES);
  // ATTENDED shows the tracked duration, e.g. "Attended (1 hr 30 mins)"; the rest are static labels.
  const label =
    status === UserShiftStatus.ATTENDED
      ? t(`${TABLE_I18N}.userStatus.ATTENDED`, {
          time: formatAttendanceTimeDisplay(attendanceMinutes ?? 0, t),
        })
      : t(`${TABLE_I18N}.userStatus.${status}`);
  return <StatusIndicator type={USER_STATUS_INDICATOR[status]}>{label}</StatusIndicator>;
}

function LocationCell({
  shift,
  eventLocationLabel,
  virtualLabel,
}: {
  shift: Shift;
  eventLocationLabel: string;
  virtualLabel: string;
}) {
  return <>{getShiftLocationLabel(shift, eventLocationLabel, virtualLabel)}</>;
}

/**
 * The viewer's effective IANA zone: useTimezone()'s value when it is a valid zone, else the
 * browser-resolved zone. useTimezone() can surface an unvalidated value (e.g. a ?timezone= URL
 * param) that would reach Intl toLocaleString and throw RangeError, so validate it at the source —
 * mirroring the isValidTimeZone guard applied to event.timeZone in resolveShiftDisplayZone. Both the
 * Date and Time columns route through this, so they degrade to the same browser-resolved zone on a
 * malformed value instead of crashing a cell.
 */
function resolveViewerZone(rawViewerZone: string): string {
  return isValidTimeZone(rawViewerZone)
    ? rawViewerZone
    : Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Resolves the IANA zone a shift's date and time should render in, so the Date and Time columns
 * agree on the calendar day near a timezone day boundary. Returns both the location-anchored
 * `shiftZone` (undefined when there is none) and the `displayZone` the cells format in: callers that
 * only render a value use `displayZone`, while the Time cell also needs `shiftZone` to decide its
 * viewer/event mismatch popover.
 */
function resolveShiftDisplayZone(
  event: Event,
  shift: Shift,
  viewerZone: string
): { shiftZone: string | undefined; displayZone: string } {
  // The shift's effective location: its own only when it opts out of the event location, otherwise
  // the event's (mirrors getShiftLocationLabel so the Time and Location cells agree on the location).
  const shiftLocation = shift.useEventLocation === false ? shift.location : event.location;
  const isVirtual = shiftLocation?.locationType === LocationType.VIRTUAL;

  // Zone precedence. A physical shift renders in the event's stored IANA zone: the Location model
  // carries no per-shift timezone, so event.timeZone is the only location-anchored zone available
  // (its reliability is bounded by the upstream geocoding bug, SIM b14a82cc). A virtual shift — or a
  // physical shift with no stored event zone — renders in the viewer's local zone with no popover,
  // since there is no location zone to contrast against the viewer's.
  //
  // Guard on isValidTimeZone: an unknown/malformed event.timeZone (the SIM b14a82cc reliability risk)
  // would otherwise reach toLocaleString({ timeZone }) and throw RangeError. An invalid zone falls
  // through to the viewer zone (no popover) for BOTH the cell text and the mismatch decision.
  const shiftZone =
    !isVirtual && event.timeZone && isValidTimeZone(event.timeZone) ? event.timeZone : undefined;
  return { shiftZone, displayZone: shiftZone ?? viewerZone };
}

function TimeCell({ event, shift }: { event: Event; shift: Shift }) {
  const { t } = useLocalizedText(TABLE_NAMESPACES);
  const locale = useLocale();
  const rawViewerZone = useTimezone();
  const viewerZone = resolveViewerZone(rawViewerZone);

  // shiftZone (the location-anchored zone, undefined when it falls back) is still needed here, beyond
  // displayZone, to decide the viewer/event mismatch popover below.
  const { shiftZone, displayZone } = resolveShiftDisplayZone(event, shift, viewerZone);

  const start = formatShiftTime(shift.startDateTime, displayZone, locale);
  const end = formatShiftTime(shift.endDateTime, displayZone, locale);

  const timeText = start && end ? `${start} - ${end}` : start;
  if (!timeText) return <>{t(`${TABLE_I18N}.ongoing`)}</>;

  // Coerce the shift start to a Date for the offset comparison (Timestamp is a Date at runtime; the
  // string branch is defensive). The mismatch is decided at the shift instant, so DST is respected.
  const startDate = toDate(shift.startDateTime);

  // Flag a mismatch only for a physical shift whose zone sits at a DIFFERENT UTC offset than the
  // viewer's AT the shift instant — an offset comparison, not IANA string equality, so two zones that
  // share an offset at that instant (e.g. Europe/London and UTC in winter) never trigger the popover.
  // The mismatch is judged at the shift START instant, so a shift that crosses a DST boundary is
  // classified on its start offset.
  const showMismatch =
    shiftZone !== undefined && doZonesDifferAtInstant(startDate, shiftZone, viewerZone);
  if (!showMismatch) return <>{timeText}</>;

  return (
    <Popover
      header={t(`${TABLE_I18N}.timezoneMismatch.header`)}
      triggerType="text"
      size="large"
      content={
        <KeyValuePairs
          columns={2}
          items={[
            {
              label: t(`${TABLE_I18N}.timezoneMismatch.eventLocalTime`),
              value: formatShiftTimeRangeWithZone(
                startDate,
                toDate(shift.endDateTime),
                shiftZone,
                locale
              ),
            },
            {
              label: t(`${TABLE_I18N}.timezoneMismatch.yourLocalTime`),
              value: formatShiftTimeRangeWithZone(
                startDate,
                toDate(shift.endDateTime),
                viewerZone,
                locale
              ),
            },
          ]}
        />
      }
    >
      {timeText}
    </Popover>
  );
}

/** Coerce a Timestamp to a Date (a Date at runtime; the string branch is defensive). */
function toDate(value: Date | string | undefined): Date | undefined {
  if (!value) return undefined;
  return value instanceof Date ? value : new Date(value);
}

function SpotsCell({ shift, dimmed }: { shift: Shift; dimmed: boolean }) {
  const { t } = useLocalizedText(TABLE_NAMESPACES);
  // maxSignup of 0/undefined means unlimited capacity.
  if (!shift.maxSignup)
    return <CellText dimmed={false}>{t(`${TABLE_I18N}.unlimitedSpots`)}</CellText>;
  const signedUp = shift.maxSignup - shift.signupsRemaining;
  const full = isShiftFull(shift);
  return (
    <SpaceBetween direction="horizontal" size="xs">
      <CellText dimmed={dimmed}>
        {t(`${TABLE_I18N}.spots`, { count: signedUp, max: shift.maxSignup })}
      </CellText>
      {full && <StatusIndicator type="warning">{t(`${TABLE_I18N}.full`)}</StatusIndicator>}
    </SpaceBetween>
  );
}

/**
 * Renders the row's single action, delegating to the existing reusable controls. getShiftAction is
 * the authority on which one shows, so each branch maps 1:1 to a control (ShiftActions already
 * bundles ToggleSignup + its complex-signup dialog; ToggleWaitlist owns the join dialog).
 */
function ActionCell({
  event,
  shift,
  action,
  signup,
  attendance,
  waitlistEntry,
  linkStyle,
  forceJoinWaitlist,
  forceLeaveWaitlist,
  forceCancelSignup,
  source,
}: {
  event: Event;
  shift: Shift;
  action: ShiftAction;
  signup?: Signup;
  attendance?: Attendance;
  waitlistEntry?: WaitlistEntry;
  linkStyle?: boolean;
  /** One-click join deep link matched this row (fires its current Sign Up / Join Waitlist action). */
  forceJoinWaitlist?: boolean;
  /** One-click leave deep link matched this row's ACTIVE waitlist entry. */
  forceLeaveWaitlist?: boolean;
  /** One-click cancel deep link matched this row (fires its Cancel sign up action). */
  forceCancelSignup?: boolean;
  source?: SignupSurface;
}) {
  switch (action) {
    case ShiftAction.CANCEL_OR_TRACK:
      return (
        <CancelOrTrackActions
          event={event}
          shift={shift}
          signup={signup}
          attendance={attendance}
          forceCancelSignup={forceCancelSignup}
        />
      );
    case ShiftAction.SIGN_UP:
    case ShiftAction.CANCEL_SIGNUP:
      return (
        <ShiftActions
          event={event}
          shift={shift}
          signup={signup}
          attendance={attendance}
          linkStyle={linkStyle}
          forceSignup={forceJoinWaitlist}
          forceCancelSignup={forceCancelSignup}
          source={source}
        />
      );
    case ShiftAction.TRACK_TIME:
      return (
        <TrackAttendanceLink
          shift={shift}
          signup={signup}
          attendance={attendance}
          linkStyle={linkStyle}
        />
      );
    case ShiftAction.JOIN_WAITLIST:
      return (
        <ToggleWaitlist
          event={event}
          shift={shift}
          isOnWaitlist={false}
          forceJoinWaitlist={forceJoinWaitlist}
        />
      );
    case ShiftAction.LEAVE_WAITLIST:
      return (
        <ToggleWaitlist
          event={event}
          shift={shift}
          isOnWaitlist
          waitlistEntry={waitlistEntry}
          forceLeaveWaitlist={forceLeaveWaitlist}
        />
      );
    default:
      return (
        <Box textAlign="center" color="text-status-inactive">
          -
        </Box>
      );
  }
}

/**
 * Volunteer-facing shift table — the counterpart to CoordinatorEventShiftsTable. Renders the event's
 * shifts with the volunteer's per-shift status and action. Reused on the Event Details page, the
 * Search result cards, and My Activity > Volunteer (callers pre-filter shifts and pass the user's
 * signups/attendances/waitlist entries).
 */
export function VolunteerEventShiftsTable(props: VolunteerEventShiftsTableProps) {
  const { t } = useLocalizedText(TABLE_NAMESPACES);
  const locale = useLocale();
  const rawViewerZone = useTimezone();
  const viewerZone = resolveViewerZone(rawViewerZone);
  const {
    event,
    shifts,
    signups,
    attendances,
    waitlistEntries,
    pageSize = DEFAULT_SHIFT_TABLE_PAGE_SIZE,
    pageSizeOptions = SHIFT_TABLE_PAGE_SIZE_OPTIONS,
    statusFilter,
    forceJoinWaitlistShiftId,
    forceLeaveWaitlistEntryId,
    forceCancelShiftId,
    variant,
    header,
    source,
  } = props;

  const [searchParams] = useSearchParams();
  const features = useFeatures();
  // Gate the inline-link action style on ShiftWaitlist so only the new volunteer table renders link
  // actions (matching CoordinatorEventShiftsTable); the shared action controls keep their buttons in
  // their other consumers (the carousel, My Activity signup cards).
  const isShiftWaitlistEnabled = featureIsEnabled(SHIFT_WAITLIST, features, searchParams);

  // Derived IDENTICALLY to the ToggleSignup click path (actions.tsx) so the exposure denominator and
  // the signup-click numerator share the same cohort bucket.
  const isSearchActivityShiftTableOn = featureIsEnabled(
    SEARCH_AND_ACTIVITY_SHIFT_TABLE,
    features,
    searchParams
  );

  // Freeze the default grouped order for the session so a status change (register / join / leave /
  // track) updates the row's content in place instead of re-floating it to a new group — which reads
  // as "register -> cancel -> register" and is worse across pages (phanhenr, CR-285081914). The grouped
  // order is recomputed only on a fresh start: a page refresh / remount, or navigating to a different
  // event (seededEventIdRef below). An explicit column sort (useCollection) still reorders live on top.
  const frozenOrderRef = useRef<string[] | null>(null);
  // The event this freeze was seeded for. If the table instance is reused for a different event (SPA
  // navigation between event pages re-renders rather than remounts), reset so the new event re-seeds
  // its own grouped order instead of inheriting the previous event's positions.
  const seededEventIdRef = useRef<string | undefined>(undefined);

  // Resolve each shift to a row (status + action) — always from the latest inputs so the status/action
  // cells stay current — then apply the optional per-page status filter. The row ORDER comes from the
  // frozen seed (below), not a re-sort, so a mid-session status change never moves the row.
  //
  // Seed contract: the frozen order is captured from the FIRST build that has rows, so the caller must
  // pass the user's signups/attendances/waitlistEntries alongside the shifts on that render (the Event
  // Details wrapper gates on all of them being fetched). Passing shifts before that data resolves would
  // freeze a pre-signup grouping for the session.
  const rows = useMemo(() => {
    const built = shifts.map((shift) =>
      buildShiftRow(
        shift,
        signups,
        waitlistEntries,
        attendances,
        Date.now(),
        isShiftWaitlistEnabled
      )
    );
    const filtered = statusFilter ? built.filter((row) => statusFilter(row.userStatus)) : built;

    // Reused for a different event -> drop the previous event's frozen order and re-seed below.
    if (seededEventIdRef.current !== event.eventId) {
      seededEventIdRef.current = event.eventId;
      frozenOrderRef.current = null;
    }
    // Seed the frozen order from the default grouping on the first build that has data.
    if (frozenOrderRef.current === null && filtered.length > 0) {
      frozenOrderRef.current = sortShiftRows(filtered).map((row) => row.shift.shiftId);
    }
    const frozenOrder = frozenOrderRef.current;
    if (!frozenOrder) return sortShiftRows(filtered); // no data yet — nothing to freeze

    // Keep seeded rows in their frozen positions; any shift added after the seed (rare — e.g. an
    // ingestor update mid-session) is not in the frozen order, so order those by the default grouping
    // and append them.
    const frozenRank = new Map(frozenOrder.map((shiftId, index) => [shiftId, index]));
    const seeded = filtered
      .filter((row) => frozenRank.has(row.shift.shiftId))
      // frozenRank.has (in the filter) guarantees a defined rank; the ?? fallback only satisfies the
      // type without a non-null assertion (unreachable — a missing id would sort to the tail of the
      // seeded rows).
      .sort(
        (a, b) =>
          (frozenRank.get(a.shift.shiftId) ?? frozenOrder.length) -
          (frozenRank.get(b.shift.shiftId) ?? frozenOrder.length)
      );
    const added = sortShiftRows(filtered.filter((row) => !frozenRank.has(row.shift.shiftId)));
    return [...seeded, ...added];
  }, [
    event.eventId,
    shifts,
    signups,
    waitlistEntries,
    attendances,
    statusFilter,
    isShiftWaitlistEnabled,
  ]);

  // Denominator mirror of the SIGNUP_COHORT_METRIC ('shifts_signup_cohort') click numerator emitted by
  // ToggleSignup (actions.tsx): one exposure per surface view, keyed with the same `${cohort}:${source}`
  // value, so signup click-through = SIGNUP_COHORT_METRIC clicks / SIGNUP_EXPOSURE_METRIC exposures.
  const exposureEmittedRef = useRef(false);
  const exposedEventIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    // Reset the once-per-view latch when this table instance is reused for a different event (SPA
    // navigation between event pages re-renders rather than remounts — see seededEventIdRef above),
    // so the new event emits its own exposure instead of staying latched from the previous one. This
    // keeps the treatment denominator symmetric with the carousel control across event switches.
    if (exposedEventIdRef.current !== event.eventId) {
      exposedEventIdRef.current = event.eventId;
      exposureEmittedRef.current = false;
    }
    if (exposureEmittedRef.current) return;
    // Hold the emit until feature flags resolve: useFeatures() is null while loading and
    // featureIsEnabled then reads false, so emitting now buckets a genuine treatment user as control
    // and the latch never corrects it once flags arrive, skewing the A/B denominator.
    if (features === null) return;
    // Skip empty views: gate on the rendered row count AFTER the status filter (rows), not the raw
    // shifts prop, so a filter that excludes every shift (empty table) does not emit and inflate the
    // denominator. Mirrors the carousel's filteredShifts guard.
    if (rows.length === 0) return;
    exposureEmittedRef.current = true;
    const cohort = isSearchActivityShiftTableOn ? SIGNUP_COHORT_TREATMENT : SIGNUP_COHORT_CONTROL;
    publishKatalMetric(SIGNUP_EXPOSURE_METRIC, source ? `${cohort}:${source}` : cohort);
  }, [event.eventId, rows.length, isSearchActivityShiftTableOn, features, source]);

  const virtualLabel = t(`${TABLE_I18N}.virtualLocation`);
  const eventLocationLabel = resolveLocationLabel(event.location, virtualLabel, '');

  const columnMap: ColumnMap<VolunteerShiftRow> = {
    shiftName: (row) => (
      <CellText dimmed={isRowDimmed(row)}>
        <TruncatedCellText text={row.shift.name} />
      </CellText>
    ),
    shiftStatus: (row) => <ShiftStatusCell status={row.shiftStatus} />,
    location: (row) => (
      <CellText dimmed={isRowDimmed(row)}>
        <LocationCell
          shift={row.shift}
          eventLocationLabel={eventLocationLabel}
          virtualLabel={virtualLabel}
        />
      </CellText>
    ),
    date: (row) => (
      <CellText dimmed={isRowDimmed(row)}>
        {/* Render the date in the shift's resolved display zone (the same zone the Time cell uses) so
            the Date and Time columns never disagree on the calendar day across a timezone boundary. */}
        {formatShiftDate(
          row.shift.startDateTime,
          resolveShiftDisplayZone(event, row.shift, viewerZone).displayZone,
          locale
        )}
      </CellText>
    ),
    time: (row) => (
      <CellText dimmed={isRowDimmed(row)}>
        <TimeCell event={event} shift={row.shift} />
      </CellText>
    ),
    spots: (row) => <SpotsCell shift={row.shift} dimmed={isRowDimmed(row)} />,
    yourStatus: (row) => (
      <UserStatusCell status={row.userStatus} attendanceMinutes={row.attendance?.minutes} />
    ),
    action: (row) => (
      <ActionCell
        event={event}
        shift={row.shift}
        action={row.action}
        signup={row.signup}
        attendance={row.attendance}
        waitlistEntry={row.waitlistEntry}
        linkStyle={isShiftWaitlistEnabled}
        source={source}
        forceJoinWaitlist={
          !forceDeliveredRef.current.join && row.shift.shiftId === forceJoinWaitlistShiftId
        }
        forceLeaveWaitlist={
          !forceDeliveredRef.current.leave &&
          forceLeaveWaitlistEntryId !== undefined &&
          row.waitlistEntry?.waitlistEntryId === forceLeaveWaitlistEntryId
        }
        forceCancelSignup={
          !forceDeliveredRef.current.cancel && row.shift.shiftId === forceCancelShiftId
        }
      />
    ),
  };

  const { columnDefinitions } = useTableColumns(
    columnMap,
    TABLE_I18N,
    TABLE_NAMESPACES,
    undefined,
    {
      shiftName: SHIFT_NAME_COLUMN_WIDTH_PX,
    }
  );
  // Attach a stable sortingField + comparator to each sortable column (Action has neither, so it
  // stays caret-less).
  const sortComparators = getVolunteerShiftSortComparators(eventLocationLabel, virtualLabel);
  const sortableColumnDefinitions = columnDefinitions.map((column) => {
    // Action has no comparator; sortComparators is typed Record<string, Fn> so the lookup is never
    // undefined to the compiler — check key presence with `in` rather than a truthiness check.
    const id = column.id;
    // useTableColumns rebuilds these column objects on every render. Cloudscape tracks the active
    // sort column by reference, so without a render-stable identity it loses the sorted column after
    // each re-render — the caret disappears and clicking the same header again restarts at ascending
    // instead of toggling to descending. A stable sortingField (the column id) gives the column a
    // render-independent identity so the toggle works, matching the sortable ambassadorDirectoryTable.
    return id && id in sortComparators
      ? { ...column, sortingField: id, sortingComparator: sortComparators[id] }
      : column;
  });

  const preferencesStrings = usePreferencesStrings(
    `${TABLE_I18N}.preferences`,
    pageSizeOptions,
    TABLE_NAMESPACES
  );
  // pageSize is the initial value; the settings (gear) preferences let the user change it per session.
  const [currentPageSize, setCurrentPageSize] = useState(pageSize);

  const { items, paginationProps, collectionProps, actions } = useCollection(rows, {
    sorting: {},
    pagination: { pageSize: currentPageSize },
  });

  // A one-click deep link targets ONE shift, but the table paginates at currentPageSize rows. If the
  // target sits on a later page its row never mounts, so its control never fires and the email link
  // silently does nothing. The flat per-signup list this replaced avoided that by asking the server
  // for the target first (priorityShiftId); here the whole event is already in hand, so page to the
  // target instead. Runs before the delivery latch below can mark the flag delivered, because the
  // latch only fires once the target is among the mounted items. Inert without a flag, so the default
  // first-page view is unchanged.
  const forcePageAlignedRef = useRef(false);
  useEffect(() => {
    if (forcePageAlignedRef.current) return;
    if (!forceJoinWaitlistShiftId && !forceLeaveWaitlistEntryId && !forceCancelShiftId) return;
    const targetIndex = rows.findIndex(
      (row) =>
        (!!forceJoinWaitlistShiftId && row.shift.shiftId === forceJoinWaitlistShiftId) ||
        (!!forceLeaveWaitlistEntryId &&
          row.waitlistEntry?.waitlistEntryId === forceLeaveWaitlistEntryId) ||
        (!!forceCancelShiftId && row.shift.shiftId === forceCancelShiftId)
    );
    // Not resolved yet (data still loading) or targets another event — retry on the next build.
    if (targetIndex < 0) return;
    forcePageAlignedRef.current = true;
    actions.setCurrentPage(Math.floor(targetIndex / currentPageSize) + 1);
  }, [
    rows,
    currentPageSize,
    forceJoinWaitlistShiftId,
    forceLeaveWaitlistEntryId,
    forceCancelShiftId,
    actions,
  ]);

  // Deliver each one-click deep-link force flag at most ONCE per table mount. The once-guards inside
  // the action controls are per component instance, and a mutation-driven action flip swaps the
  // control family (ToggleSignup <-> ToggleWaitlist unmount/remount) — the URL flag outlives that,
  // so a fresh control would re-fire it (e.g. leaving after a join deep link would auto-rejoin the
  // waitlist). Latch on the flag's target appearing among the MOUNTED page items: effects run after
  // the commit in which the target row's control mounted (its own effect has already consumed the
  // flag), so from the next render the flag reads false and any later remount stays inert.
  const forceDeliveredRef = useRef({ join: false, leave: false, cancel: false });
  useEffect(() => {
    const delivered = forceDeliveredRef.current;
    if (
      forceJoinWaitlistShiftId &&
      items.some((row) => row.shift.shiftId === forceJoinWaitlistShiftId)
    ) {
      delivered.join = true;
    }
    if (
      forceLeaveWaitlistEntryId &&
      items.some((row) => row.waitlistEntry?.waitlistEntryId === forceLeaveWaitlistEntryId)
    ) {
      delivered.leave = true;
    }
    if (forceCancelShiftId && items.some((row) => row.shift.shiftId === forceCancelShiftId)) {
      delivered.cancel = true;
    }
  }, [items, forceJoinWaitlistShiftId, forceLeaveWaitlistEntryId, forceCancelShiftId]);

  // Default 'container' variant <Table> in a plain <div>, mirroring CoordinatorEventShiftsTable;
  // callers embedded in an existing card pass variant='embedded' (Search). resizableColumns lets the
  // user drag columns: the Shift Name column starts at SHIFT_NAME_COLUMN_WIDTH_PX and can be widened
  // to read long names (TruncatedCellText clips to the current width), while the sticky last Action
  // column fills the remaining space. The table scrolls horizontally when space is tight.
  return (
    <div>
      <Table
        {...collectionProps}
        variant={variant}
        header={header}
        columnDefinitions={sortableColumnDefinitions}
        resizableColumns
        items={items}
        stickyColumns={{ last: 1 }}
        pagination={<Pagination {...paginationProps} />}
        preferences={
          <CollectionPreferences
            {...preferencesStrings}
            preferences={{ pageSize: currentPageSize }}
            onConfirm={({ detail }) => setCurrentPageSize(detail.pageSize ?? pageSize)}
          />
        }
        data-testid="volunteer-event-shifts-table"
        empty={
          <Box textAlign="center" color="inherit">
            {t(`${TABLE_I18N}.empty`)}
          </Box>
        }
      />
    </div>
  );
}
